import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SubscribeDialog } from "@/components/billing/SubscribeDialog";
import { DEFAULT_ACCENT_COLOR } from "@/lib/accentColor";
import { useEntitlements } from "@/hooks/useEntitlements";
import { getPlanPrices } from "@/lib/pricing";
import { useT } from "@/lib/translation";
import { cn } from "@/lib/utils";

const MS_PER_DAY = 86_400_000;
/**
 * Gold subscribe button for the top bar. Free users see the plain offer, users on the app trial
 * see how long the trial has left. Paid, founding and friends & family users never see it.
 */
export function UpgradeButton({ className }: { className?: string }) {
  const { tier, trialEndsAt, freeTierEnabled, isFounding } = useEntitlements();
  const t = useT();

  if (tier === "paid") return null;
  if (tier === "free" && !freeTierEnabled) return null;

  const { onSale, salePercentOff } = getPlanPrices(isFounding);
  const daysLeft =
    tier === "trial" && trialEndsAt
      ? Math.max(
          1,
          Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / MS_PER_DAY),
        )
      : null;

  // The sale label wins over the trial countdown while it runs: it is the shorter deadline.
  const label = onSale
    ? t("billing:plan.saleBadge", { percent: salePercentOff })
    : daysLeft === null
      ? t("billing:upgradeButton.free")
      : t("billing:upgradeButton.trial", { count: daysLeft });

  return (
    <SubscribeDialog>
      <Button
        size="sm"
        className={cn(
          "gap-1.5 border-0 text-white hover:text-white hover:brightness-95",
          className,
        )}
        style={{ backgroundColor: DEFAULT_ACCENT_COLOR }}
      >
        <Sparkles className="h-4 w-4" />
        {label}
      </Button>
    </SubscribeDialog>
  );
}

export default UpgradeButton;
